import { useState, useMemo } from 'react'
import { Search, ChevronDown, ChevronUp, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TemplateCard } from './template-card'
import { BlockCatalogPanel } from './block-catalog-panel'
import { TierFilterPills } from './tier-filter-pills'
import type { TemplateSummary } from '@/features/page-editor/services/template-loader'
import { GalleryBrainstormCta } from '@/features/brainstorm/entries'
import { useStudioLocale } from '@/i18n'
import { localizeTemplateDomain } from '@/features/project/lib/template-description-copy'

interface ExploreGalleryProps {
  templates: TemplateSummary[]
  onOpenTemplate: (id: string) => void
  onUseTemplate: (templateId: string) => void
}

export function ExploreGallery({ templates, onOpenTemplate, onUseTemplate }: ExploreGalleryProps) {
  const copy = useStudioLocale().strings.starters.gallery
  const catalog = useStudioLocale().strings.starters.catalog
  const [search, setSearch] = useState('')
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null)
  const [selectedTier, setSelectedTier] = useState<'all' | 'A' | 'B'>('all')
  const [blockCatalogOpen, setBlockCatalogOpen] = useState(false)

  const domains = useMemo(() => {
    const set = new Set(templates.map((t) => t.domain))
    return Array.from(set).sort()
  }, [templates])

  const filtered = useMemo(() => {
    let result = templates
    if (selectedTier !== 'all') {
      result = result.filter((t) => t.tier === selectedTier)
    }
    if (selectedDomain) {
      result = result.filter((t) => t.domain === selectedDomain)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q) ||
          t.tags.some((tag) => tag.toLowerCase().includes(q)),
      )
    }
    return result
  }, [templates, selectedTier, selectedDomain, search])

  return (
    <div className="space-y-6">
      {/* Search + brainstorm CTA */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={copy.searchPlaceholder}
            className="h-10 w-full rounded-xl border border-border bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        {/* Brainstorm escape hatch — surfaces curated alternatives outside
            the static template grid. Mock provider today; Workstream D
            swaps in real findCandidates. */}
        <GalleryBrainstormCta
          pattern={selectedDomain ?? undefined}
        />
      </div>

      {/* Tier + Domain filter */}
      <div className="flex items-center gap-4 overflow-x-auto pb-1">
        <TierFilterPills selected={selectedTier} onChange={setSelectedTier} />
        <div className="w-px h-6 bg-border" />
        <button
          onClick={() => setSelectedDomain(null)}
          className={cn(
            'inline-flex min-h-10 items-center rounded-full px-4 text-xs font-medium whitespace-nowrap transition-colors',
            selectedDomain === null
              ? 'bg-foreground text-background'
              : 'bg-muted text-muted-foreground hover:text-foreground',
          )}
        >
          {copy.all}
        </button>
        {domains.map((d) => (
          <button
            key={d}
            onClick={() => setSelectedDomain(d === selectedDomain ? null : d)}
            className={cn(
              'inline-flex min-h-10 items-center rounded-full px-4 text-xs font-medium whitespace-nowrap transition-colors',
              d === selectedDomain
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground hover:text-foreground',
            )}
          >
            {localizeTemplateDomain(d, catalog)}
          </button>
        ))}
      </div>

      {/* Template grid */}
      {filtered.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((t) => (
            <TemplateCard
              key={t.id}
              template={t}
              onOpen={onOpenTemplate}
              onUseTemplate={(tmpl) => onUseTemplate(tmpl.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 space-y-1">
          <p className="text-sm font-medium text-foreground">{copy.emptyTitle}</p>
          <p className="text-xs text-muted-foreground">{copy.emptyBody}</p>
        </div>
      )}

      {/* Block Catalog collapsible */}
      <div className="border border-border rounded-xl overflow-hidden">
        <button
          onClick={() => setBlockCatalogOpen(!blockCatalogOpen)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-accent/50 transition-colors"
        >
          <span>{copy.blockCatalog}</span>
          {blockCatalogOpen ? (
            <ChevronUp className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        {blockCatalogOpen && (
          <div className="border-t border-border p-4">
            <BlockCatalogPanel />
          </div>
        )}
      </div>

      {/* Community placeholder */}
      <div className="rounded-xl border border-border/50 p-6 text-center bg-muted/30">
        <Users className="w-8 h-8 text-muted-foreground/30 mx-auto" />
        <p className="mt-2 text-sm font-medium text-muted-foreground">{copy.communityTemplates}</p>
        <p className="text-xs text-muted-foreground/70">{copy.comingSoon}</p>
      </div>
    </div>
  )
}
